import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { B2BTeamFilters, B2BTeamMember } from '../../modules/b2b/team/types';

export interface B2BTeamState {
  members: B2BTeamMember[];
  activeMemberId: string | null;
  activeMember: B2BTeamMember | null;
  filters: B2BTeamFilters;
  isLoading: boolean;
  isLoadingActiveMember: boolean;
  isSaving: boolean;
  deletingMemberId: string | null;
}

const initialState: B2BTeamState = {
  members: [],
  activeMemberId: null,
  activeMember: null,
  filters: {
    query: '',
  },
  isLoading: false,
  isLoadingActiveMember: false,
  isSaving: false,
  deletingMemberId: null,
};

export const name = 'b2bTeam';

const b2bTeamSlice = createSlice({
  name,
  initialState,
  reducers: {
    setMembers(state, { payload }: PayloadAction<B2BTeamMember[]>) {
      state.members = payload;
    },
    setActiveMemberId(state, { payload }: PayloadAction<string | null>) {
      state.activeMemberId = payload;
    },
    setActiveMember(state, { payload }: PayloadAction<B2BTeamMember | null>) {
      state.activeMember = payload;
    },
    clearActiveMember(state) {
      state.activeMemberId = null;
      state.activeMember = null;
      state.isLoadingActiveMember = false;
    },
    addMember(state, { payload }: PayloadAction<B2BTeamMember>) {
      state.members.unshift(payload);
    },
    replaceMember(state, { payload }: PayloadAction<B2BTeamMember>) {
      const memberIndex = state.members.findIndex(
        ({ id }) => id === payload.id
      );

      if (memberIndex !== -1) {
        state.members[memberIndex] = payload;
      }

      if (state.activeMemberId === payload.id) {
        state.activeMember = payload;
      }
    },
    removeMember(state, { payload }: PayloadAction<string>) {
      state.members = state.members.filter(({ id }) => id !== payload);

      if (state.activeMemberId === payload) {
        state.activeMemberId = null;
        state.activeMember = null;
      }
    },
    setFilters(state, { payload }: PayloadAction<Partial<B2BTeamFilters>>) {
      state.filters = { ...state.filters, ...payload };
    },
    setIsLoading(state, { payload }: PayloadAction<boolean>) {
      state.isLoading = payload;
    },
    setIsLoadingActiveMember(state, { payload }: PayloadAction<boolean>) {
      state.isLoadingActiveMember = payload;
    },
    setIsSaving(state, { payload }: PayloadAction<boolean>) {
      state.isSaving = payload;
    },
    setDeletingMemberId(state, { payload }: PayloadAction<string | null>) {
      state.deletingMemberId = payload;
    },
  },
});

export const {
  addMember,
  clearActiveMember,
  removeMember,
  replaceMember,
  setActiveMember,
  setActiveMemberId,
  setDeletingMemberId,
  setFilters,
  setIsLoading,
  setIsLoadingActiveMember,
  setIsSaving,
  setMembers,
} = b2bTeamSlice.actions;

export default b2bTeamSlice.reducer;
